import { Heart, Music, Settings, Target, TrendingUp, Zap } from "lucide-react";
import type React from "react";
import Seo from "../components/Seo";

const Experience: React.FC = () => {
  const experiences = [
    {
      icon: <Settings className="w-12 h-12" />,
      title: "Tension Dial Control",
      description:
        "Adjust the resistance on your bike to match the rhythm, tailoring your workout intensity while staying in sync with the music.",
      color: "bg-blue-100",
    },
    {
      icon: <Music className="w-12 h-12" />,
      title: "Rhythmic Flow & BPM Sync",
      description:
        "Every interval aligns with the tempo of the music, guiding your efforts and motivating you to push through sprints, climbs, and everything in between.",
      color: "bg-purple-100",
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: "Endurance Testing",
      description:
        "Classes gradually challenge and test stamina over time with carefully timed intervals of high resistance and faster-paced efforts.",
      color: "bg-green-100",
    },
  ];

  return (
    <div
      className="min-h-full"
      style={{ backgroundColor: "var(--color-complementary)" }}
    >
      <Seo
        title="The Experience"
        description="Discover the Cycle Collective experience: rhythm-based indoor cycling with BPM-synced intervals, tension control, and endurance-building classes for all levels."
        path="/experience"
      />
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{ color: "var(--color-text)" }}
            >
              The Cycle Co Experience
            </h1>
            <p className="text-lg text-black/70 max-w-3xl mx-auto">
              Discover what makes our indoor cycling classes uniquely engaging
              and effective
            </p>
          </div>

          {/* Experience Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {experiences.map((experience, index) => (
              <div
                key={index}
                className="bg-white/50 backdrop-blur-sm rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow"
              >
                <div
                  className={`w-20 h-20 ${experience.color} rounded-full flex items-center justify-center mx-auto mb-6`}
                >
                  <div style={{ color: "var(--color-text)" }}>
                    {experience.icon}
                  </div>
                </div>
                <h3
                  className="text-xl font-bold mb-4"
                  style={{ color: "var(--color-text)" }}
                >
                  {experience.title}
                </h3>
                <p className="text-black/80 leading-relaxed">
                  {experience.description}
                </p>
              </div>
            ))}
          </div>

          {/* Benefits Section */}
          <div className="bg-white/30 backdrop-blur-sm rounded-xl p-8 mb-12">
            <h2
              className="text-2xl font-bold text-center mb-8"
              style={{ color: "var(--color-text)" }}
            >
              Why Choose Cycle Co?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart className="w-6 h-6 text-red-600" />
                </div>
                <h4
                  className="font-semibold mb-2"
                  style={{ color: "var(--color-text)" }}
                >
                  Heart-Pumping Workouts
                </h4>
                <p className="text-sm text-black/70">
                  Challenging yet achievable classes for all fitness levels
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Music className="w-6 h-6 text-yellow-600" />
                </div>
                <h4
                  className="font-semibold mb-2"
                  style={{ color: "var(--color-text)" }}
                >
                  Music-Driven Energy
                </h4>
                <p className="text-sm text-black/70">
                  Motivational playlists that keep you moving
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <h4
                  className="font-semibold mb-2"
                  style={{ color: "var(--color-text)" }}
                >
                  Track Your Progress
                </h4>
                <p className="text-sm text-black/70">
                  See improvements in endurance and performance
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-white/30 backdrop-blur-sm rounded-xl p-8 max-w-2xl mx-auto">
              <Zap className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
              <h3
                className="text-xl font-semibold mb-3"
                style={{ color: "var(--color-text)" }}
              >
                Ready to Experience the Difference?
              </h3>
              <p className="text-black/70 mb-6">
                Join our community and discover why cyclists keep coming back
                for more.
              </p>
              <a
                href="/services"
                className="inline-block px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:scale-105 shadow-lg"
                style={{
                  backgroundColor: "var(--color-altBrand)",
                  color: "var(--color-text)",
                }}
              >
                Book Your First Class
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Experience;
